/**
 * Разрешения AR-ведения (датчики + камера). iOS выдаёт их только из обработчика
 * тапа, поэтому модуль статический (НЕ в ленивом чанке): между тапом и
 * запросом не должно быть `await import()`. Поток камеры AR-чанк забирает
 * через takeWarmup() (ждёт летящий запрос — гонка «тап → мгновенный старт»
 * не теряет поток); если человек передумал — dropWarmup().
 */
import type { SensorAccess } from '../nav/orientation'

export interface ArWarmup {
  readonly sensors: SensorAccess
  readonly stream: MediaStream | null
}

let warm: ArWarmup | null = null
let inflight: Promise<ArWarmup> | null = null
let inflightWithCamera = false
/** Поколение разогрева: dropWarmup отменяет и ЛЕТЯЩИЙ запрос — поздний
 *  «Разрешить» в немодальном промпте не воскрешает камеру после отказа от AR. */
let warmupGen = 0

type PermissionRequester = { requestPermission?: () => Promise<'granted' | 'denied'> }

async function requestSensors(): Promise<SensorAccess> {
  if (typeof DeviceOrientationEvent === 'undefined') return 'unsupported'
  const requester = DeviceOrientationEvent as unknown as PermissionRequester
  if (typeof requester.requestPermission !== 'function') return 'granted'
  try {
    return (await requester.requestPermission()) === 'granted' ? 'granted' : 'denied'
  } catch {
    return 'denied'
  }
}

async function doWarmup(withCamera: boolean, gen: number): Promise<ArWarmup> {
  // Уже разогрето в нужном составе (повторный тап) — не мигаем камерой заново.
  if (warm && (!withCamera || warm.stream)) return warm
  const sensors = await requestSensors()
  let stream: MediaStream | null = null
  if (withCamera && navigator.mediaDevices?.getUserMedia) {
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: 'environment' } },
        audio: false,
      })
    } catch {
      stream = null // отказ или камеры нет — деградация до карты, без AR-картинки
    }
  }
  const result: ArWarmup = { sensors, stream }
  if (gen !== warmupGen) {
    // Пока висел промпт, от AR отказались — поток гасим, не сохраняем.
    for (const track of stream?.getTracks() ?? []) track.stop()
    return result
  }
  warm = result
  return result
}

/** Запросить всё нужное для AR прямо в тапе. Отказ в камере — легальный исход:
 *  ведение честно деградирует до карты (тост ar.no_camera). Конкурентные
 *  вызовы получают один запрос; «с камерой» поверх летящего «без камеры»
 *  дожидается его и дозапрашивает камеру — состав не теряется. */
export function warmupAr(withCamera: boolean): Promise<ArWarmup> {
  if (inflight && (inflightWithCamera || !withCamera)) return inflight
  const gen = warmupGen
  const prev = inflight
  const run = (async () => {
    if (prev) await prev.catch(() => undefined)
    return doWarmup(withCamera, gen)
  })().finally(() => {
    if (inflight === run) {
      inflight = null
      inflightWithCamera = false
    }
  })
  inflight = run
  inflightWithCamera = withCamera || inflightWithCamera
  return run
}

/** Забрать разогретые разрешения (одноразово — владение потоком переходит).
 *  Дожидается летящего warmupAr — тап «Иду ✓» стартует ведение мгновенно. */
export async function takeWarmup(): Promise<ArWarmup | null> {
  if (inflight) await inflight.catch(() => undefined)
  const taken = warm
  warm = null
  return taken
}

/** Человек передумал (вышел из ориентации, выбрал карту, ЧС) — камеру гасим,
 *  включая ещё летящий запрос (см. warmupGen). */
export function dropWarmup(): void {
  warmupGen++
  for (const track of warm?.stream?.getTracks() ?? []) track.stop()
  warm = null
}
